<template>
    <Card class="export-history">
        <div class="export-history__header">
            <Heading
                :labelTitle="$t.__('export.history.title')"
                variant="h2"
                :prefixColoredIcon="{
                    icon: 'history',
                    color: CompassEnums.ColoredIconColorType.BRAND,
                }"
            />
            <CompassButton
                :label="$t.__('common.actions.export_the_list')"
                :type="CompassEnums.ButtonType.SECONDARY"
                :loading="isExporting"
                @click="handleExport"
            />
        </div>

        <TableLoading v-if="isLoading && exportItems.length === 0" />

        <template v-else-if="exportItems.length > 0">
            <TableLoading v-if="isLoading" />
            <CompassTable v-else :isCheckable="false" :border="false">
                <CompassTableHead>
                    <CompassTableHeadCell>
                        {{ translations.date }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell>
                        {{ translations.name }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell>
                        {{ translations.collaborator }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell>
                        {{ translations.rowCount }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell>
                        {{ translations.progress }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell id="actionColumn" fixed="right" />
                </CompassTableHead>

                <CompassTableBody>
                    <CompassTableRow v-for="row in exportItems" :key="row.id">
                        <CompassTableBodyCell>
                            {{ formatDate(row.created_at) }}
                        </CompassTableBodyCell>
                        <CompassTableBodyCell>
                            <CompassLink
                                v-if="row.public_url"
                                :label="getExportName(row)"
                                :link="row.public_url"
                                target="_blank"
                            />
                            <template v-else>
                                {{ getExportName(row) }}
                            </template>
                        </CompassTableBodyCell>
                        <CompassTableBodyCell>
                            {{ getOwnerFullName(row.owner_id) }}
                        </CompassTableBodyCell>
                        <CompassTableBodyCell>
                            {{ row.total_rows ?? '' }}
                        </CompassTableBodyCell>
                        <CompassTableBodyCell>
                            <ExportProgressColumn
                                :status="row.status"
                                :progress="row.progress ?? 0"
                                :started_at="row.started_at ? moment(row.started_at) : undefined"
                            />
                        </CompassTableBodyCell>
                        <CompassTableBodyCell idColumn="actionColumn">
                            <CompassMenu
                                placement="bottom-end"
                                customClass="export-history-action-menu"
                            >
                                <CompassIconButton
                                    icon="action"
                                    :size="CompassEnums.IconButtonSize.SMALL"
                                />
                                <template #dropdown>
                                    <CompassMenuItem
                                        :label="$t.__('export.listing.actions.rename')"
                                        prefixIcon="edit"
                                        @click="openRenameModal(row)"
                                    />
                                    <CompassMenuItem
                                        v-if="row.public_url"
                                        :label="$t.__('export.notifications.download')"
                                        prefixIcon="download"
                                        @click="handleDownload(row.public_url ?? '')"
                                    />
                                    <CompassMenuItem
                                        :label="$t.__('export.listing.actions.reschedule')"
                                        prefixIcon="sync"
                                        @click="handleReschedule(row.id)"
                                    />
                                </template>
                            </CompassMenu>
                        </CompassTableBodyCell>
                    </CompassTableRow>
                </CompassTableBody>
            </CompassTable>

            <TablePagination
                v-show="!isLoading"
                :isLoading="isLoading"
                :pagination="pagination"
                @update-pagination="handlePaginationChange"
            />
        </template>

        <CompassEmptyState
            v-else-if="!isLoading"
            illustrationPath="/ressources/logged/img/illustrations/no-results.svg"
            :title="$t.__('export.listing.emptyPlaceholder')"
        />
    </Card>
</template>

<script setup lang="ts">
import Card from 'vcomponents/common/card/Card.vue';
import TableLoading from 'vcomponents/common/table/TableLoading.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';
import TablePagination from 'vcomponents/common/table/TablePagination.vue';
import ExportProgressColumn from 'vcomponents/listing/tableColumns/ExportProgressColumn.vue';
import useListingNames from 'vcomponents/listing/apiListing/mapping/ListingNames';
import {
    searchExports,
    getExport,
    getMetadata,
    rescheduleExport,
    exportList,
} from 'api/notification/Export';
import type { ExportItem, ExportOwner } from 'api/notification/Export';
import vueFilters from 'filters/vue_filters';
import moment from 'moment';
import { ref, onMounted, onUnmounted, inject } from 'vue';
import { useStore } from 'vuex';
import type { Translator, PushToast } from 'js/shims-vue';

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;
const store = useStore();

const listingNames = useListingNames($t);

const PER_PAGE = 25;

const exportItems = ref<ExportItem[]>([]);
const owners = ref<ExportOwner[]>([]);
const isLoading = ref(false);
const isExporting = ref(false);
const pagination = ref({
    limit: PER_PAGE,
    offset: 0,
    total: 0,
    currentPage: 1,
});

const translations: Record<string, string> = {
    date: $t.__('export.listing.columns.date'),
    name: $t.__('export.listing.columns.name'),
    collaborator: $t.__('export.listing.columns.collaborator'),
    rowCount: $t.__('export.listing.columns.rowCount'),
    progress: $t.__('export.listing.columns.progress'),
};

const formatDate = (date: string): string => {
    return vueFilters.dateFormatter(date, 'SLASHED_DATE_TIME');
};

const getExportName = (row: ExportItem): string => {
    if (row.custom_name) {
        return row.custom_name;
    }

    const date = vueFilters.dateFormatter(row.created_at, 'SLASHED_DATE_TIME', false);
    const resolvedName = listingNames[row.listing_name]
        ? listingNames[row.listing_name].toLowerCase()
        : row.listing_name;

    return $t.__('export.notifications.exportName', { type: resolvedName, date });
};

const getOwnerFullName = (ownerId: string): string => {
    const owner = owners.value.find((owner) => owner.id === ownerId);
    return owner ? `${owner.firstname} ${owner.lastname}` : '';
};

const fetchExports = async (page: number = 1, limit: number = PER_PAGE): Promise<void> => {
    isLoading.value = true;
    try {
        const response = await searchExports({
            offset: limit * (page - 1),
            limit,
            order: 'created_at',
            direction: 'desc',
        });

        exportItems.value = response.data.data;

        pagination.value = {
            limit,
            offset: (page - 1) * limit,
            total: response.data.pagination.total,
            currentPage: page,
        };
    } catch {
        $pushToast({
            type: CompassEnums.ToastType.ERROR,
            icon: true,
            text: $t.__('common.labels.toast.error'),
        });
    } finally {
        isLoading.value = false;
    }
};

const fetchMetadata = async (): Promise<void> => {
    try {
        const response = await getMetadata();
        owners.value = response.data.owners;
    } catch {
        // metadata is non-critical
    }
};

interface PaginationUpdate {
    limit: number;
    offset: number;
    total: number;
    currentPage: number;
}

const handlePaginationChange = (newPagination: PaginationUpdate): void => {
    pagination.value = {
        ...newPagination,
        offset: (newPagination.currentPage - 1) * newPagination.limit,
    };
    fetchExports(newPagination.currentPage, newPagination.limit);
};

const handleExport = async (): Promise<void> => {
    isExporting.value = true;
    try {
        await exportList();
        $pushToast({
            type: CompassEnums.ToastType.SUCCESS,
            text: $t.__('export.history.processing'),
            icon: true,
        });
        fetchExports(1);
    } catch {
        $pushToast({
            type: CompassEnums.ToastType.ERROR,
            text: $t.__('export.history.launchError'),
            icon: true,
        });
    } finally {
        isExporting.value = false;
    }
};

const handleDownload = (url: string): void => {
    window.open(url, '_blank');
};

const handleReschedule = async (id: string): Promise<void> => {
    try {
        await rescheduleExport(id);
        $pushToast({
            type: CompassEnums.ToastType.SUCCESS,
            text: $t.__('export.history.processing'),
            icon: true,
        });
        fetchExports(1);
    } catch (error: unknown) {
        const axiosError = error as { response?: { status: number } };
        if (axiosError.response && axiosError.response.status === 423) {
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                text: $t.__('export.history.alreadyRunning'),
                icon: true,
            });
        } else {
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                text: $t.__('export.history.launchError'),
                icon: true,
            });
        }
    }
};

const openRenameModal = (row: ExportItem): void => {
    store.dispatch('popupPanel/open', {
        key: 'exportRenameModal',
        props: {
            exportId: row.id,
            exportName: getExportName(row),
            callback: () => fetchExports(pagination.value.currentPage, pagination.value.limit),
        },
    });
};

const onProgressChange = async ({ id }: { id: string }): Promise<void> => {
    const index = exportItems.value.findIndex((exportItem) => exportItem.id === id);
    if (index === -1) {
        return;
    }

    try {
        const response = await getExport(id);
        exportItems.value[index] = response.data;
    } catch {
        // silent — SSE will retry
    }
};

const onExport = (): void => {
    fetchExports(1);
};

onMounted(() => {
    fetchMetadata();
    fetchExports();
    EventBus.on('listing:export', onExport as (...args: unknown[]) => void);
    EventBus.on('export:progress', onProgressChange as (...args: unknown[]) => void);
});

onUnmounted(() => {
    EventBus.off('listing:export', onExport as (...args: unknown[]) => void);
    EventBus.off('export:progress', onProgressChange as (...args: unknown[]) => void);
});
</script>

<style lang="scss" scoped>
.export-history {
    &__empty {
        color: var(--colors-neutral-500);
        margin: 0;
        padding: var(--spacing-4);
        text-align: center;
    }

    &__header {
        align-items: center;
        display: flex;
        justify-content: space-between;
        margin: 0 0 var(--spacing-3);

        &:deep(.heading) {
            margin-bottom: 0;
        }
    }
}
</style>
<style lang="scss">
.export-history-action-menu {
    z-index: 1000;
}
</style>
